import { useMemo } from 'react'
import { X } from 'lucide-react'
import type { SnapPoint } from '../../lib/snap-points'
import type { WorkOffsetRegister } from '../../types'

const ALL_REGISTERS: WorkOffsetRegister[] = ['G54', 'G55', 'G56', 'G57', 'G58', 'G59']
const MAX_CHIPS = 8

interface WorkOffsetPickerOverlayProps {
  snapPoints: SnapPoint[]
  hoveredPosition: { x: number; y: number; z: number } | null
  selectedSnapId: string | null
  register: WorkOffsetRegister
  onSelectSnap: (id: string) => void
  onRegisterChange: (r: WorkOffsetRegister) => void
  onConfirm: () => void
  onClose: () => void
}

export default function WorkOffsetPickerOverlay({
  snapPoints,
  hoveredPosition,
  selectedSnapId,
  register,
  onSelectSnap,
  onRegisterChange,
  onConfirm,
  onClose,
}: WorkOffsetPickerOverlayProps) {
  // Show up to MAX_CHIPS: corners + center first, then holes
  const { visibleChips, overflowCount } = useMemo(() => {
    const corners = snapPoints.filter(p => p.category === 'corner')
    const centers = snapPoints.filter(p => p.category === 'center')
    const holes = snapPoints.filter(p => p.category === 'hole')
    const ordered = [...corners, ...centers, ...holes]
    const visible = ordered.slice(0, MAX_CHIPS)
    return { visibleChips: visible, overflowCount: Math.max(0, ordered.length - MAX_CHIPS) }
  }, [snapPoints])

  const selectedSnap = selectedSnapId ? snapPoints.find(p => p.id === selectedSnapId) : null
  const displayPos = selectedSnap?.position ?? hoveredPosition

  return (
    <div className="absolute bottom-3 left-3 z-10 rounded-xl border border-cyan-500/20 bg-[#030712]/80 backdrop-blur-sm px-4 py-3 max-w-sm">
      {/* Header */}
      <div className="flex items-center justify-between mb-2.5">
        <span className="font-mono text-[10px] text-slate-400 tracking-widest uppercase">Set Work Origin</span>
        <div className="flex items-center gap-2">
          <select
            value={register}
            onChange={(e) => onRegisterChange(e.target.value as WorkOffsetRegister)}
            className="font-mono text-[10px] text-cyan-300 bg-transparent border border-cyan-500/20 rounded px-1.5 py-0.5 outline-none cursor-pointer"
          >
            {ALL_REGISTERS.map(r => (
              <option key={r} value={r} className="bg-[#030712]">{r}</option>
            ))}
          </select>
          <button
            onClick={onClose}
            className="text-slate-600 hover:text-slate-400 transition-colors"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Suggestion chips */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {visibleChips.map((snap) => {
          const isActive = selectedSnapId === snap.id
          return (
            <button
              key={snap.id}
              onClick={() => onSelectSnap(snap.id)}
              className={`
                rounded-full text-[10px] px-2.5 py-1 border transition-colors duration-150 font-mono
                ${isActive
                  ? 'border-amber-400/50 bg-amber-500/15 text-amber-300'
                  : 'border-cyan-500/20 text-cyan-300/70 hover:border-cyan-500/40 hover:text-cyan-300'
                }
              `}
            >
              {snap.label}
            </button>
          )
        })}
        {overflowCount > 0 && (
          <span className="rounded-full text-[10px] px-2.5 py-1 border border-cyan-500/10 text-slate-600 font-mono">
            +{overflowCount}
          </span>
        )}
      </div>

      {/* Coordinate readout */}
      <div className="flex items-center gap-4 mb-3 font-mono text-xs tabular-nums">
        <div className="flex items-center gap-1.5">
          <span className="text-slate-600">X</span>
          <span className="text-cyan-300">{displayPos ? displayPos.x.toFixed(3) : '—'}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-slate-600">Y</span>
          <span className="text-cyan-300">{displayPos ? displayPos.y.toFixed(3) : '—'}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-slate-600">Z</span>
          <span className="text-cyan-300">{displayPos ? displayPos.z.toFixed(3) : '—'}</span>
        </div>
      </div>

      {/* Confirm button */}
      <button
        onClick={onConfirm}
        disabled={!selectedSnapId && !hoveredPosition}
        className="w-full py-1.5 rounded-lg border border-cyan-500/30 bg-cyan-500/5 hover:bg-cyan-500/10 font-mono text-xs text-cyan-300 transition-colors duration-150 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        Set Origin
      </button>
    </div>
  )
}
